import React from 'react';
import { t } from '@lingui/macro';
import {
  ListItem,
  Text,
  UnorderedList,
  useDisclosure,
} from '@cardboard-ui/react';
import { graphql, useMutation } from 'utils/graphClient';
import { Owner } from 'components/ownership/Owner';
import { useSession } from 'utils/sessionProvider';
import { OwnerPanel, OwnerPanelTitle } from '../ownership/OwnerPanel';
import { AddOwnerModal } from 'components/ownership/AddOwnerModal';
import { notEmpty } from 'utils/NotEmptyFilter';
import { ExplicitOwners_addEventOwner_Mutation } from './__generated__/ExplicitOwners_addEventOwner_Mutation.graphql';
import { ExplicitOwners_removeEventOwner_Mutation } from './__generated__/ExplicitOwners_removeEventOwner_Mutation.graphql';
import { ExplicitOwners_removeFolderNodeOwner_Mutation } from './__generated__/ExplicitOwners_removeFolderNodeOwner_Mutation.graphql';
import { ExplicitOwners_addAnnouncementOwner_Mutation } from './__generated__/ExplicitOwners_addAnnouncementOwner_Mutation.graphql';
import { ExplicitOwners_removeAnnouncementOwner_Mutation } from './__generated__/ExplicitOwners_removeAnnouncementOwner_Mutation.graphql';
import { OwnersPanelProps } from './SpaceItemAccessDetails';
import { ExplicitOwners_addFolderNodeOwner_Mutation } from './__generated__/ExplicitOwners_addFolderNodeOwner_Mutation.graphql';

const ownershipFragment = graphql`
  fragment ExplicitOwners_ownership on HasOwnerPermissionsInterface {
    ownerships {
      edges {
        node {
          ...Owner_ownership
          explicitOwner
          member {
            id
            status {
              value
            }
          }
          ownerOf {
            edges {
              node {
                __typename
              }
            }
          }
        }
      }
    }
  }
`;

const addOwnerToFolderNodeMutation = graphql`
  mutation ExplicitOwners_addFolderNodeOwner_Mutation(
    $nodeId: ID!
    $memberId: ID!
  ) {
    addFolderNodeOwner(input: { folderNodeId: $nodeId, memberId: $memberId }) {
      folderNodeUpdated {
        ... on Folder {
          id
        }
        ...ExplicitOwners_ownership
      }
    }
  }
`;

const addOwnerToEventMutation = graphql`
  mutation ExplicitOwners_addEventOwner_Mutation($nodeId: ID!, $memberId: ID!) {
    addEventOwner(input: { eventId: $nodeId, memberId: $memberId }) {
      eventUpdated {
        id
        ...ExplicitOwners_ownership
      }
    }
  }
`;

const addOwnerToAnnouncementMutation = graphql`
  mutation ExplicitOwners_addAnnouncementOwner_Mutation(
    $nodeId: ID!
    $memberId: ID!
  ) {
    addAnnouncementOwner(
      input: { announcementId: $nodeId, memberId: $memberId }
    ) {
      announcementUpdated {
        id
        ...ExplicitOwners_ownership
      }
    }
  }
`;

const removeOwnerFromFolderNodeMutation = graphql`
  mutation ExplicitOwners_removeFolderNodeOwner_Mutation(
    $nodeId: ID!
    $memberId: ID!
  ) {
    removeFolderNodeOwner(
      input: { folderNodeId: $nodeId, memberId: $memberId }
    ) {
      removedOwnerMemberId
      folderNodeUpdated {
        ... on Folder {
          id
        }
        ...ExplicitOwners_ownership
      }
    }
  }
`;

const removeOwnerFromEventMutation = graphql`
  mutation ExplicitOwners_removeEventOwner_Mutation(
    $nodeId: ID!
    $memberId: ID!
  ) {
    removeEventOwner(input: { eventId: $nodeId, memberId: $memberId }) {
      removedOwnerMemberId
      eventUpdated {
        id
        ... on HasOwnerPermissionsInterface {
          ...ExplicitOwners_ownership
        }
      }
    }
  }
`;

const removeOwnerFromAnnouncementMutation = graphql`
  mutation ExplicitOwners_removeAnnouncementOwner_Mutation(
    $nodeId: ID!
    $memberId: ID!
  ) {
    removeAnnouncementOwner(
      input: { announcementId: $nodeId, memberId: $memberId }
    ) {
      removedOwnerMemberId
      announcementUpdated {
        id
        ... on HasOwnerPermissionsInterface {
          ...ExplicitOwners_ownership
        }
      }
    }
  }
`;

type OwnerOfType = 'Event' | 'Folder' | 'FolderItem';

interface ExplicitOwnersPanelProps extends OwnersPanelProps {
  spaceId: string;
  canModifyOwners: boolean;
  ownerOfType: OwnerOfType;
}

export function isOwnerOfType(type: string): type is OwnerOfType {
  switch (type) {
    case 'Event':
    case 'FolderItem':
    case 'Announcement':
    case 'Folder':
      return true;
    default:
      return false;
  }
}

export const ExplicitOwners = ({
  ownerships,
  canModifyOwners,
  nodeId,
  spaceId,
  ownerOfType,
}: ExplicitOwnersPanelProps) => {
  const { isCurrentMember } = useSession();

  const [addFolderNodeOwner, isFolderNodeAddOwnerLoading] =
    useMutation<ExplicitOwners_addFolderNodeOwner_Mutation>(
      addOwnerToFolderNodeMutation,
    );

  const [removeFolderNodeOwner, isFolderNodeRemoveOwnerLoading] =
    useMutation<ExplicitOwners_removeFolderNodeOwner_Mutation>(
      removeOwnerFromFolderNodeMutation,
    );

  const [addEventOwner, isEventAddOwnerLoading] =
    useMutation<ExplicitOwners_addEventOwner_Mutation>(addOwnerToEventMutation);

  const [addAnnouncementOwner, isAnnouncementAddOwnerLoading] =
    useMutation<ExplicitOwners_addAnnouncementOwner_Mutation>(
      addOwnerToAnnouncementMutation,
    );

  const [removeEventOwner, isEventRemoveOwnerLoading] =
    useMutation<ExplicitOwners_removeEventOwner_Mutation>(
      removeOwnerFromEventMutation,
    );

  const [removeAnnouncementOwner, isAnnouncementRemoveOwnerLoading] =
    useMutation<ExplicitOwners_removeAnnouncementOwner_Mutation>(
      removeOwnerFromAnnouncementMutation,
    );

  const isAddOwnerLoading =
    isFolderNodeAddOwnerLoading || isEventAddOwnerLoading;
  const isRemoveOwnerLoading =
    isFolderNodeRemoveOwnerLoading || isEventRemoveOwnerLoading;

  const removeOwner =
    {
      Event: removeEventOwner,
      Folder: removeFolderNodeOwner,
      FolderItem: removeFolderNodeOwner,
      Announcement: removeAnnouncementOwner,
    }[ownerOfType] ||
    ((e: any) => {
      console.log('error', e);
      throw `Can not remove owners of ${ownerOfType}`;
    });
  const addOwner =
    {
      Event: addEventOwner,
      Folder: addFolderNodeOwner,
      FolderItem: addFolderNodeOwner,
      Announcement: addAnnouncementOwner,
    }[ownerOfType] ||
    ((e: any) => {
      console.log('error', e);
      throw `Can not add owners too ${ownerOfType}`;
    });

  const { isOpen, onClose, onOpen } = useDisclosure();

  const explicitOwners =
    ownerships?.edges
      ?.filter(notEmpty)
      .filter(
        (owner) =>
          owner?.node?.explicitOwner &&
          owner.node.member.status?.value !== 'DELETED',
      ) || [];

  const onAddOwners = (newOwnerIds: string[], onSuccess: () => void) => {
    newOwnerIds.forEach((newOwnerId, index) => {
      addOwner({
        variables: {
          nodeId: nodeId,
          memberId: newOwnerId,
        },
        onCompleted: () => {
          if (index === newOwnerIds.length - 1) {
            onSuccess();
          }
        },
      });
    });
  };

  const createOnRemoveOwner = (memberId: string) => {
    return (onSuccess: () => void) => {
      removeOwner({
        variables: {
          memberId,
          nodeId: nodeId,
        },
        onCompleted: onSuccess,
      });
    };
  };

  return (
    <>
      <OwnerPanel
        title={
          <OwnerPanelTitle
            onAddOwners={canModifyOwners ? onOpen : undefined}
          >{t`Explicit owners`}</OwnerPanelTitle>
        }
      >
        {explicitOwners.length === 0 ? (
          <Text>{t`No explicit owners`}</Text>
        ) : (
          explicitOwners.map((owner) => {
            const memberId = owner?.node?.member?.id;
            if (!memberId) {
              return null;
            }
            return (
              <Owner
                key={memberId}
                ownership={owner!.node!}
                canRemoveOwner={
                  canModifyOwners && !isCurrentMember({ id: memberId })
                }
                onRemoveOwner={createOnRemoveOwner(memberId)}
                isRemoveOwnerLoading={isRemoveOwnerLoading}
              />
            );
          })
        )}
      </OwnerPanel>
      {isOpen && (
        <AddOwnerModal
          isOpen={isOpen}
          onClose={onClose}
          isAddOwnersLoading={isAddOwnerLoading}
          currentOwnerIds={explicitOwners.map(
            (owner) => owner.node?.member.id || '',
          )}
          onAddOwners={onAddOwners}
          spaceId={spaceId}
        >
          <UnorderedList>
            <ListItem>{t`Add and remove other owners`}</ListItem>
            <ListItem>{t`View, modify and delete this item.`}</ListItem>
          </UnorderedList>
        </AddOwnerModal>
      )}
    </>
  );
};
